import React, { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { useSubscription } from '../hooks/useSubscription';

const brandPrimary = '#29458d';
const brandPrimaryHover = '#1e3266';

const primaryBtnSx = {
  backgroundColor: brandPrimary,
  color: '#fff',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 400,
  padding: '10px 20px',
  borderRadius: '6px',
  boxShadow: 'none',
  '&:hover': { backgroundColor: brandPrimaryHover, boxShadow: 'none' },
};

const outlinedBtnSx = {
  color: brandPrimary,
  borderColor: brandPrimary,
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 400,
  padding: '10px 20px',
  borderRadius: '6px',
  '&:hover': { borderColor: brandPrimaryHover, backgroundColor: 'rgba(41, 69, 141, 0.06)' },
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return null;
  }
}

export default function CheckoutSuccess() {
  const { subscription, loading, isActive, refresh } = useSubscription();
  const [polls, setPolls] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (isActive || polls > 10) return undefined;
    const id = setTimeout(() => {
      refresh();
      setPolls((p) => p + 1);
    }, 1500);
    return () => clearTimeout(id);
  }, [isActive, polls, refresh]);

  // Stripe charges immediately when the trial ends, so trial end = first billing date.
  const trialEnd = formatDate(subscription?.trial_end);
  const firstChargeDate = trialEnd;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 2, mb: 3, px: 2 }}>
      <Paper sx={{ p: 3, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 600, color: brandPrimary, mb: 1 }}>
          {isActive ? "You're subscribed! 🎉" : 'Finalizing your subscription…'}
        </Typography>
        {isActive && (
          <Typography sx={{ fontSize: '14px', color: '#6c757d', mb: 3 }}>
            Thanks for upgrading to Pre Go Prep Premium.
          </Typography>
        )}

        {loading || (!isActive && polls < 10) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CircularProgress size={20} sx={{ color: brandPrimary }} />
          </Box>
        ) : isActive ? (
          <>
            {trialEnd && (
              <Typography sx={{ fontSize: '15px', mb: 1 }}>
                Your free trial runs until <strong>{trialEnd}</strong>. You will not be charged until then.
              </Typography>
            )}
            {firstChargeDate && (
              <Typography sx={{ fontSize: '15px', mb: 2 }}>
                Your first charge will be on <strong>{firstChargeDate}</strong> unless you cancel before that date.
              </Typography>
            )}
            <Typography sx={{ fontSize: '15px', mb: 2 }}>
              You can update your payment method or cancel anytime from{' '}
              <Button
                variant="text"
                component={RouterLink}
                to="/profile"
                sx={{ p: 0, minWidth: 'auto', fontSize: '15px', textTransform: 'none', color: brandPrimary, fontWeight: 600, verticalAlign: 'baseline' }}
              >
                Manage Subscription
              </Button>{' '}
              in your Profile.
            </Typography>
            {subscription?.price_tier === 'early_supporter' && (
              <Alert severity="success" sx={{ mb: 2, fontSize: '14px' }}>
                You're on the early supporter rate.
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="info" sx={{ mb: 2, fontSize: '14px' }}>
            We're still confirming your subscription. This usually finishes within a minute — check your Profile in a moment.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
          <Button variant="contained" onClick={() => navigate('/calculations')} sx={primaryBtnSx}>
            Go to Calculator
          </Button>
          <Button variant="outlined" component={RouterLink} to="/profile" sx={outlinedBtnSx}>
            Manage Subscription
          </Button>
        </Box>

        <Typography sx={{ fontSize: '13px', color: '#6c757d', mt: 3 }}>
          Something not right?{' '}
          <RouterLink to="/about-contact#contact" style={{ color: brandPrimary, fontWeight: 600, textDecoration: 'underline' }}>
            Contact us
          </RouterLink>{' '}
          and we'll help you sort it out.
        </Typography>
      </Paper>
    </Box>
  );
}
